import { useState, useEffect } from 'react';
import Modal from '../Common/Modal';

export default function CounterProposalModal({ isOpen, onClose, onSubmit, isLoading, originalTermin }) {
  // minDate auf heute — Disponenten brauchen Flexibilität für Same-Day-Vorschläge
  // (Notfälle, kurzfristige Slot-Lücken). Bei „heute" zusätzlich Uhrzeit-Sperre auf
  // >= jetzt, weil das min-Attribut auf type=time von Browsern nicht zuverlässig
  // erzwungen wird (Desktop-Chrome lässt vergangene Werte zu) — daher echte JS-
  // Validierung mit sichtbarem Fehler statt stillem Submit-Guard.
  const [datum, setDatum] = useState('');
  const [uhrzeit, setUhrzeit] = useState('09:00');
  const [submitError, setSubmitError] = useState('');

  // „Jetzt" wird beim Öffnen des Modals + jede Minute frisch evaluiert,
  // damit ein 30 Minuten geöffnet liegen gebliebenes Modal keine stale Uhrzeit
  // als Limit ausweist.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!isOpen) return;
    setNow(new Date());
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const pad = (n) => String(n).padStart(2, '0');
  const minDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const isToday = datum === minDate;
  const minTime = isToday ? `${pad(now.getHours())}:${pad(now.getMinutes())}` : undefined;

  // Fehler verschwindet sobald der Nutzer Werte korrigiert.
  useEffect(() => { setSubmitError(''); }, [datum, uhrzeit]);

  // Wenn der Nutzer „heute" auswählt, aber die aktuelle Uhrzeit (Default 09:00
  // oder vorheriger Wert) bereits in der Vergangenheit liegt, springe automatisch
  // auf den nächsten 15-Min-Slot mit 15 Minuten Puffer ab jetzt. Damit muss der
  // Disponent nicht manuell korrigieren — der Default „09:00" war für gestern
  // gedacht, für heute Nachmittag ist „nächste viertel Stunde" sinnvoller.
  const handleDatumChange = (e) => {
    const newDatum = e.target.value;
    setDatum(newDatum);
    if (newDatum === minDate) {
      const nowMs = new Date();
      nowMs.setMinutes(nowMs.getMinutes() + 15);
      const roundedMinutes = Math.ceil(nowMs.getMinutes() / 15) * 15;
      if (roundedMinutes === 60) {
        nowMs.setHours(nowMs.getHours() + 1);
        nowMs.setMinutes(0);
      } else {
        nowMs.setMinutes(roundedMinutes);
      }
      const candidate = `${pad(nowMs.getHours())}:${pad(nowMs.getMinutes())}`;
      // Nur überschreiben wenn der bestehende Wert in der Vergangenheit liegt —
      // hat der Disponent schon eine sinnvolle Zukunfts-Uhrzeit gewählt, lassen.
      if (uhrzeit < candidate) {
        setUhrzeit(candidate);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!datum) {
      setSubmitError('Bitte ein Datum auswählen.');
      return;
    }
    if (datum < minDate) {
      setSubmitError('Das Datum darf nicht in der Vergangenheit liegen.');
      return;
    }
    if (isToday && uhrzeit <= minTime) {
      setSubmitError(`Bei heutigem Datum bitte eine Uhrzeit nach ${minTime} wählen.`);
      return;
    }
    // YYYY-MM-DD → DD.MM.YYYY
    const [y, m, d] = datum.split('-');
    onSubmit({ datum: `${d}.${m}.${y}`, uhrzeit });
  };

  const handleClose = () => {
    setDatum('');
    setUhrzeit('09:00');
    setSubmitError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Gegenvorschlag senden" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        {originalTermin && (
          <div className="text-sm text-text-muted bg-gray-50 rounded-lg p-3">
            Ursprünglich gewünscht: <span className="font-medium text-text">{originalTermin}</span>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-text mb-1">Datum</label>
          <input
            type="date"
            value={datum}
            onChange={handleDatumChange}
            min={minDate}
            required
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-text mb-1">
            Uhrzeit
            {isToday && minTime && (
              <span className="ml-2 text-xs text-text-muted font-normal">(heute frühestens nach {minTime})</span>
            )}
          </label>
          <input
            type="time"
            value={uhrzeit}
            onChange={(e) => setUhrzeit(e.target.value)}
            min={minTime}
            required
            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        {submitError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {submitError}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-text-muted bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!datum || isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition disabled:opacity-50"
          >
            {isLoading ? 'Wird gesendet...' : 'Vorschlag senden'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
